import Vue from 'vue'
import Vuex from 'vuex'
import axios from 'axios'
import router from '@/router'
import { eventBus } from '@/main'

Vue.use(Vuex)

const quizAxios = axios.create({
  baseURL: process.env.VUE_APP_QUIZ || '',
})

const cabinetAxios = axios.create({
  baseURL: process.env.VUE_APP_USERS || '',
})

const CABINET_TOKEN_NAME = 'id_token'

function setCabinetHeader(token) {
  cabinetAxios.defaults.headers.common[
    "Authorization"
  ] = `Bearer  ${token}`
}

let callSearchCity

export default {
  namespaced: true,
  state: {
    dataReady: false,

    uid: undefined,
    firstName: undefined,
    lastName: undefined,
    cabinetUser: {},
    photoIsUploaded: undefined,
    ingredients: [],
    ingredientsVocab: {},

    answers: {},
    questions: [],
    currentOrder: 1,
    questionsProgress: 0,
    ingredientsProgress: 0,

    locationShown: false,
    foundLocations: [],
    environment: {},
    environmentLocation: '',

    responses: {
      getUser: false,
      sendUserCredentials: false,
      getQuestions: false,
      getIngredients: false,
      sendAnswer: false,
      sendPhoto: false,
      searchLocation: false,
      getEnvironment: false,
    }
  },
  mutations: {
    SAVE_QUIZ_USER(state, uid) {
      state.uid = uid
      localStorage.setItem('quiz_uid', uid)
    },

    SAVE_CABINET_USER(token) {
      localStorage.setItem(CABINET_TOKEN_NAME, token.user.token)
    },

    PURGE_CABINET_USER() {
      localStorage.removeItem(CABINET_TOKEN_NAME)
    },

    SAVE_INGREDIENTS(state, ingredients) {
      state.ingredients = ingredients
    },

    SAVE_INGREDIENTS_VOCABULARY(state, ingredients) {
      state.ingredientsVocab = ingredients
    },

    SAVE_ANSWERS(state, answers) {
      state.answers = answers
    },

    SAVE_QUESTIONS(state, questions) {
      state.questions = questions
    },

    SAVE_CURRENT_ORDER(state, order) {
      state.currentOrder = order
    },

    SAVE_QUESTIONS_PROGRESS(state, progress) {
      state.questionsProgress = progress
    },

    SAVE_INGREDIENTS_PROGRESS(state, progress) {
      state.ingredientsProgress = progress
    },

    SAVE_CREDENTIALS(state, { firstName, lastName }) {
      if (firstName && lastName) {
        state.firstName = firstName
        state.lastName = lastName

        localStorage.setItem('quiz_firstName', firstName)
        localStorage.setItem('quiz_lastName', lastName)
      } else {
        localStorage.removeItem('quiz_firstName')
        localStorage.removeItem('quiz_lastName')
      }
    },

    SAVE_PHOTO_STATE(state, status) {
      state.photoIsUploaded = status
    },

    SAVE_DATA_READY_STATE(state, status) {
      state.dataReady = status
    },

    SAVE_SEARCH_RESULTS(state, data) {
      state.foundLocations = data
    },

    CHANGE_LOCATION_SHOW_STATUS(state, status) {
      state.locationShown = status
    },

    SAVE_ENVIRONMENT(state, environment) {
      state.environment = environment
    },

    SAVE_ENVIRONMENT_LOCATION(state, location) {
      if (Object.keys(location).length > 0) {
        state.environmentLocation = `${location.name}, ${location.country}`
      } else {
        state.environmentLocation = ''
      }
    },

    SAVE_RESPONSE_STATE(state, { type, currState }) {
      state.responses[ type ] = currState
    },
  },
  actions: {
    // QUIZ STUFF //
    async initQuiz({ commit, dispatch }, uid) {
      await dispatch('getQuestions')
      await dispatch('getIngredients')

      commit('SAVE_RESPONSE_STATE', {
        type: 'getUser',
        currState: true,
      })

      const suffix = uid ? `/${uid}` : ''

      quizAxios
        .get(`${process.env.VUE_APP_START}${suffix}`)

        .then(res => {
          const data = res.data.response

          // Save user data
          commit('SAVE_QUIZ_USER', data.uid)

          // If user haven't completed test yet fill previously saved progress
          if (data.status.progress < 100) {
            if (data.results) {
              commit('SAVE_INGREDIENTS', data.results.components)
            }
            if (data.answers) {
              commit('SAVE_ANSWERS', data.answers)
              commit('SAVE_QUESTIONS_PROGRESS', data.status.progress)
              commit('SAVE_INGREDIENTS_PROGRESS', data.status.comp_perc)

              let lastAnswer = Object.keys(data.answers)[Object.keys(data.answers).length - 1]

              if (lastAnswer) {
                /* eslint-disable-next-line no-plusplus */
                commit('SAVE_CURRENT_ORDER', lastAnswer++ + 1)
              } else {
                commit('SAVE_CURRENT_ORDER', 1)
              }
            }
            if (data.photo) {
              commit('SAVE_PHOTO_STATE', true)
            }
            if (data.weather) {
              commit('SAVE_ENVIRONMENT', data.weather.factors)
              commit('SAVE_ENVIRONMENT_LOCATION', data.weather.location)
              commit('CHANGE_LOCATION_SHOW_STATUS', false)
            }
          }

          // If user already has saved JWT token in localStorage, send credentials
          const token = JSON.parse(atob((localStorage.getItem('id_token') || '.').split('.')[ 1 ]) || '{}')

          if (Object.keys(token).length > 0) {
            dispatch('sendUserCredentials', {
              firstName: token.name,
              lastName: token.lname,
            })
          }
          // Else send just arrived data
          else if (data.inputs) {
            dispatch('sendUserCredentials', {
              firstName: data.inputs.name,
              lastName: data.inputs.last_name,
            })
          }

          // Inform App.vue that data is saved and remove preloader
          commit('SAVE_DATA_READY_STATE', true)

          // Redirect user to certain page depending on quiz progression
          eventBus.$emit('redirectUser')
        })

        .catch(err => {
          console.error(err)
        })
    },

    async getQuestions({ commit }) {
      commit('SAVE_RESPONSE_STATE', {
        type: 'getQuestions',
        currState: true,
      })

      return new Promise((resolve, reject) => {
        quizAxios
          .get(process.env.VUE_APP_QUESTIONS)

          .then(res => {
            const data = res.data.response
            commit('SAVE_QUESTIONS', data)
            resolve(res)
          })

          .catch(err => {
            console.error(err)
            reject(err)
          })

          .finally(() => {
            commit('SAVE_RESPONSE_STATE', {
              type: 'getQuestions',
              currState: false,
            })
          })
      })
    },

    async getIngredients({ commit }) {
      commit('SAVE_RESPONSE_STATE', {
        type: 'getIngredients',
        currState: true,
      })

      return new Promise((resolve, reject) => {
        quizAxios
          .get(process.env.VUE_APP_INGREDIENTS)

          .then(res => {
            const data = res.data.response
            commit('SAVE_INGREDIENTS_VOCABULARY', data)
            resolve(res)
          })

          .catch(err => {
            console.error(err)
            reject(err)
          })

          .finally(() => {
            commit('SAVE_RESPONSE_STATE', {
              type: 'getIngredients',
              currState: false,
            })
          })
      })
    },

    sendUserCredentials({ commit, state }, { firstName, lastName }) {
      commit('SAVE_RESPONSE_STATE', {
        type: 'sendUserCredentials',
        currState: true,
      })

      commit('SAVE_CREDENTIALS', {
        firstName: firstName,
        lastName: lastName,
      })

      const payload = {
        user_id: state.uid,
        inputs: { name: firstName, last_name: lastName }
      }

      quizAxios
        .post(
          process.env.VUE_APP_CREDENTIALS,
          payload
        )

        .then(res => {
          const data = res.data.response

          commit('SAVE_RESPONSE_STATE', {
            type: 'sendUserCredentials',
            currState: false,
          })

          commit('SAVE_QUESTIONS_PROGRESS', data.status.progress)
        })

        .catch(err => {
          console.error(err)
        })
    },

    sendPhoto({ commit }, media) {
      commit('SAVE_RESPONSE_STATE', {
        type: 'sendPhoto',
        currState: true,
      })

      quizAxios
        .post(
          process.env.VUE_APP_UPLOAD_PHOTO,
          media,
          {
            headers: {
              'Content-Type': 'multipart/form-data'
            },
          },
        )

        .then(res => {
          const data = res.data.response

          if (data.uid) {
            commit('SAVE_PHOTO_STATE', true)
            commit('config/SET_CURRENT_TRANSITION', 'overlay-up-full', { root: true })
            setTimeout(() => {
              router.push('Questions')
            }, 1000)
          } else {
            commit('SAVE_PHOTO_STATE', false)
          }
        })

        .catch(err => {
          console.error(err)

          commit('SAVE_PHOTO_STATE', false)

          commit('SAVE_RESPONSE_STATE', {
            type: 'sendPhoto',
            currState: false,
          })
        })
    },

    sendAnswer({ commit, state, dispatch }, { question, answer }) {
      commit('SAVE_RESPONSE_STATE', {
        type: 'sendAnswer',
        currState: true,
      })

      const payload = {
        user_id: state.uid,
        answer: { q: question, a: answer }
      }

      quizAxios
        .post(
          process.env.VUE_APP_ANSWER,
          payload
        )
        .then(res => {
          const data = res.data.response

          commit('SAVE_RESPONSE_STATE', {
            type: 'sendAnswer',
            currState: false,
          })
          commit('SAVE_ANSWERS', data.answers)
          commit('SAVE_INGREDIENTS', data.results.components)
          commit('SAVE_INGREDIENTS_PROGRESS', data.status.comp_perc)
          commit('SAVE_QUESTIONS_PROGRESS', data.status.progress)

          Promise.all([ dispatch('verifyAuth') ])
            .then(token => {
              setCabinetHeader(token[0])

              cabinetAxios
                .post(
                  process.env.VUE_APP_USER_PROFILE,
                  { profileId: payload.user_id }
                )
                .catch(err => {
                  console.error(err)
                })
            })
            .catch(err => { console.error(err) })
        })
        .catch(err => {
          console.error(err)
        })
    },

    searchLocation({ commit }, query) {
      if (callSearchCity) {
        callSearchCity.cancel()
      }
      callSearchCity = axios.CancelToken.source()

      commit('SAVE_RESPONSE_STATE', {
        type: 'searchLocation',
        currState: true,
      })

      quizAxios
        .get(
          `${process.env.VUE_APP_SEARCH_LOCATION}/${query}`,
          {
            headers: {},
            cancelToken: callSearchCity.token,
          }
        )

        .then(res => {
          const data = res.data.response

          if (data) {
            commit('SAVE_SEARCH_RESULTS', data)
          } else {
            commit('SAVE_SEARCH_RESULTS', [])
          }

          commit('SAVE_RESPONSE_STATE', {
            type: 'searchLocation',
            currState: false,
          })
        })

        .catch(err => {
          commit('SAVE_SEARCH_RESULTS', [])

          console.error(err)

          if (!axios.isCancel(err)) {
            commit('SAVE_RESPONSE_STATE', {
              type: 'searchLocation',
              currState: false,
            })
          } else {
            commit('SAVE_RESPONSE_STATE', {
              type: 'searchLocation',
              currState: true,
            })
          }
        })
    },

    getEnvironment({ state, commit, dispatch }, location) {
      commit('SAVE_RESPONSE_STATE', {
        type: 'getEnvironment',
        currState: true,
      })

      const payload = {
        user_id: state.uid,
        location
      }

      quizAxios
        .post(
          process.env.VUE_APP_GET_ENVIRONMENT,
          payload
        )

        .then(res => {
          const data = res.data.response

          if (data) {
            commit('SAVE_ENVIRONMENT', data.weather.factors)
            commit('SAVE_ENVIRONMENT_LOCATION', data.weather.location)
            commit('SAVE_QUESTIONS_PROGRESS', data.status.progress)
            commit('CHANGE_LOCATION_SHOW_STATUS', false)

            Promise.all([ dispatch('verifyAuth') ])
              .then(token => {
                setCabinetHeader(token[0])

                cabinetAxios
                  .post(
                    process.env.VUE_APP_USER_PROFILE,
                    { profileId: payload.user_id }
                  )
                  .catch(err => {
                    console.error(err)
                  })
              })
              .catch(err => { console.error(err) })
          }
        })

        .catch(err => {
          console.error(err)
        })

        .finally(() => {
          commit('SAVE_RESPONSE_STATE', {
            type: 'getEnvironment',
            currState: false,
          })
        })
    },

    getResults() {
      const cabinetToken = localStorage.getItem(CABINET_TOKEN_NAME)

      if (cabinetToken) {
        window.location.replace(`${process.env.VUE_APP_CABINET}/cabinet/chat`)
      } else {
        window.location.replace(`${process.env.VUE_APP_CABINET}/auth/signup`)
      }
    },

    // CABINET AUTH //
    verifyAuth({ commit, dispatch }) {
      return new Promise((resolve, reject) => {
        const cabinetToken = localStorage.getItem(CABINET_TOKEN_NAME)

        if (cabinetToken) {
          let payload = JSON.parse(atob(cabinetToken.split(".")[1]))
          if (payload.exp > Date.now() / 1000) {
            resolve(cabinetToken)
          } else {
            dispatch('refreshToken')
              .then(resolve)
              .catch(err => {
                commit('PURGE_CABINET_USER')
                reject(err)
              })
          }
        } else {
          commit('PURGE_CABINET_USER')
          reject(false)
        }
      })
    },

    refreshToken() {
      return new Promise((resolve, reject) => {
        const cabinetToken = localStorage.getItem(CABINET_TOKEN_NAME)

        setCabinetHeader(cabinetToken)

        cabinetAxios.post(process.env.VUE_APP_REFRESH_TOKEN)
          .then(res => {
            const data = res.data
            if (data.success) {
              localStorage.setItem(CABINET_TOKEN_NAME, data.access_token)
              resolve(data.access_token)
            }
          })
          .catch(err => {
            console.error(err)
            reject(err)
          })
      })
    },
  },
}
